import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContentLayout from "../../component/ContentLayout";
import CustomButton from "../../component/Button";
import { VscLoading } from "react-icons/vsc";
import { Context } from "../../component/Context/ErrorContext";
import {
  FARS_DFARS_2026_INFO,
  getFarsDfars2026Initiatives,
} from "../../util/certificateBundles";
import {
  buildFarsDfarsReportEmailHtml,
  downloadFarsDfarsReportPdf,
} from "../../util/farsDfarsReportPdf";
import { getCompanyDisplayName } from "../../util/companyDisplayName";
import { fetchCompanyDashboard } from "../../util/fetchCompanyDashboard";
import { setCompanyData } from "../../store/reducers/companySlice";
import "../../styles/farsDfarsCompliance.css";

const FarsDfarsCompliance = () => {
  const dispatch = useDispatch();
  const { setError } = useContext(Context);
  const companyData = useSelector((state: any) => state?.company?.companyData);
  const companyName = useMemo(
    () => getCompanyDisplayName(companyData) || "Your Company",
    [companyData]
  );

  const [downloading, setDownloading] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const farsDfarsCerts = getFarsDfars2026Initiatives();

  const loadCompanyData = useCallback(async () => {
    try {
      const data = await fetchCompanyDashboard();
      if (data) dispatch(setCompanyData(data));
    } catch (error: any) {
      setError(error.response?.data?.message);
    }
  }, [dispatch, setError]);

  useEffect(() => {
    loadCompanyData();
  }, [loadCompanyData]);

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      await downloadFarsDfarsReportPdf(companyName);
    } catch (error) {
      console.error("Error downloading FARS/DFARS report:", error);
    } finally {
      setDownloading(false);
    }
  };

  const emailHtml = buildFarsDfarsReportEmailHtml(companyName);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailHtml);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2500);
    } catch {
      setError("Could not copy email template to clipboard.");
    }
  };

  return (
    <ContentLayout className="items-start rounded-3xl m-2 gap-6 overflow-y-scroll fars-dfars-page">
      <div className="w-full flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <p className="text-3xl font-bold text-primaryText">
            FARS/DFARS 2026 Safety Readiness
          </p>
          <p className="text-grayLight text-sm mt-1">{companyName}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CustomButton
            title={
              downloading ? (
                <VscLoading className="animate-spin" />
              ) : (
                "Download FARS/DFARS Report (PDF)"
              )
            }
            onClick={handleDownloadReport}
            disabled={downloading}
            className="!py-2"
          />
          <CustomButton
            title={emailCopied ? "Copied!" : "Copy Email Template"}
            onClick={handleCopyEmail}
            className="!py-2 !bg-gray/60"
          />
        </div>
      </div>

      <div className="w-full p-6 bg-primary rounded-3xl flex flex-col gap-3 border border-blue-500/20">
        <p className="text-primaryText text-lg font-semibold">
          {FARS_DFARS_2026_INFO.title}
        </p>
        <p className="text-sidebarSelectText text-sm font-medium">
          Effective {FARS_DFARS_2026_INFO.effectiveDate}
        </p>
        <p className="text-primaryText text-sm leading-relaxed">
          {FARS_DFARS_2026_INFO.summary}
        </p>
        <p className="text-grayLight text-xs italic">{FARS_DFARS_2026_INFO.disclaimer}</p>
      </div>

      <div className="w-full p-6 bg-primary rounded-3xl flex flex-col gap-4 fars-dfars-cert-panel">
        <p className="text-primaryText text-lg font-semibold text-left">
          Required certifications (FARS/DFARS 2026)
        </p>
        <p className="text-grayLight text-xs text-left -mt-2">
          One training completion certificate per OnMyWay protection program.
        </p>
        <div className="fars-dfars-cert-list">
          {farsDfarsCerts.map((cert, index) => (
            <div key={cert.id} className="fars-dfars-cert-row">
              <span className="fars-dfars-cert-row__index">{index + 1}.</span>
              <div className="fars-dfars-cert-row__content">
                <p className="fars-dfars-cert-row__title text-primaryText">
                  {cert.title}
                </p>
                <p className="fars-dfars-cert-row__desc">{cert.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="w-full p-6 bg-primary rounded-3xl flex flex-col gap-3">
        <p className="text-primaryText text-lg font-semibold">
          Weekly FARS/DFARS report email (preview)
        </p>
        <p className="text-grayLight text-xs">
          Example email employers can send with the PDF report attached. Use Copy
          Email Template for HTML.
        </p>
        <div
          className="p-4 rounded-xl bg-white text-gray-900 text-sm leading-relaxed max-h-[220px] overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: emailHtml }}
        />
      </div>
    </ContentLayout>
  );
};

export default FarsDfarsCompliance;
